<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted } from 'vue';
import { useBranding } from 'shared/composables/useBranding';
import { frontendURL } from 'dashboard/helper/URLHelper';
import IntegrationItem from './IntegrationItem.vue';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();

const uiFlags = getters['integrations/getUIFlags'];
const accountId = getters.getCurrentAccountId;

// Only these integrations are exposed for now; the rest stay hidden until we
// decide how to handle them. Scripts and Google Calendar are custom cards below.
const VISIBLE_INTEGRATIONS = ['webhook'];

const integrationList = computed(() =>
  getters['integrations/getAppIntegrations'].value.filter(item =>
    VISIBLE_INTEGRATIONS.includes(item.id)
  )
);

const scriptsURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/scripts`)
);

const googleCalendarURL = computed(() =>
  frontendURL(
    `accounts/${accountId.value}/settings/integrations/google_calendar`
  )
);

const scriptsCount = computed(() => {
  const crmConfig = getters['crmConfig/getCrmConfig']?.value;
  const modules = crmConfig?.config?.modules;
  return Array.isArray(modules) ? modules.filter(m => m.active).length : 0;
});

onMounted(() => {
  store.dispatch('integrations/get');
  store.dispatch('crmConfig/get');
});
</script>

<template>
  <SettingsPageLayout
    :title="$t('INTEGRATION_SETTINGS.HEADER')"
    :description="
      replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
    "
  >
    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-12"
    >
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
    </div>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <IntegrationItem
        v-for="item in integrationList"
        :id="item.id"
        :key="item.id"
        :logo="item.logo"
        :name="item.name"
        :description="item.description"
        :enabled="item.enabled"
      />
      <!-- Scripts custom card -->
      <div
        class="flex flex-col flex-1 p-5 rounded-xl border border-n-slate-3 bg-n-solid-2 shadow-sm"
      >
        <div class="flex items-start justify-between">
          <div
            class="flex items-center justify-center h-10 w-10 mb-3 rounded-lg border border-n-weak bg-n-alpha-3"
          >
            <i class="i-lucide-file-code-2 text-xl text-n-slate-11" />
          </div>
          <span
            v-tooltip="scriptsCount > 0 ? 'Ativo' : 'Inativo'"
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="scriptsCount > 0 ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-sm font-semibold">
              {{ $t('INTEGRATION_SETTINGS.SCRIPTS_CARD.TITLE') }}
            </span>
            <router-link :to="scriptsURL">
              <Button label="Configurar" link />
            </router-link>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.SCRIPTS_CARD.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <!-- Google Calendar custom card -->
      <div
        class="flex flex-col flex-1 p-5 rounded-xl border border-n-slate-3 bg-n-solid-2 shadow-sm"
      >
        <div
          class="flex items-center justify-center h-10 w-10 mb-3 rounded-lg border border-n-weak bg-n-alpha-3"
        >
          <i class="i-lucide-calendar-days text-xl text-n-slate-11" />
        </div>
        <div class="flex flex-col flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-sm font-semibold">
              {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.HEADER') }}
            </span>
            <router-link :to="googleCalendarURL">
              <Button label="Configurar" link />
            </router-link>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.DESCRIPTION') }}
          </p>
        </div>
      </div>
    </div>
  </SettingsPageLayout>
</template>
